import {Actions} from '@redstack/shared'
import {
	createFileRoute,
	stripSearchParams,
	useNavigate,
} from '@tanstack/react-router'
import {zodValidator} from '@tanstack/zod-adapter'
import {z} from 'zod'
import {DashboardPage} from '@/features/dashboard/DashboardPage'
import {DashboardSkeleton} from '@/features/dashboard/DashboardSkeleton'
import {useDashboard} from '@/hooks/stats'
import {useRequirePermission} from '@/hooks/useRequirePermission'
import type {DashboardPeriod} from '@/lib/api/stats'

const DEFAULT_PERIOD: DashboardPeriod = 'month'
const DEFAULT_OFFSET = 0

export const dashboardSearchSchema = z.object({
	period: z.enum(['month', 'quarter', 'year']).default(DEFAULT_PERIOD),
	offset: z.coerce.number().int().min(0).default(DEFAULT_OFFSET),
})

export type DashboardSearch = z.output<typeof dashboardSearchSchema>

function DashboardRouteGuard() {
	if (!useRequirePermission(Actions.DASHBOARD_VIEW)) return null
	return <DashboardRoute />
}

function DashboardRoute() {
	const {period, offset} = Route.useSearch()
	const navigate = useNavigate({from: Route.fullPath})
	const query = useDashboard(period, offset)

	function onPeriodChange(next: DashboardPeriod) {
		// Reset offset when switching period type — "2 months back" shouldn't
		// silently become "2 quarters back".
		navigate({search: (prev) => ({...prev, period: next, offset: 0})})
	}

	function onOffsetChange(next: number) {
		navigate({search: (prev) => ({...prev, offset: Math.max(0, next)})})
	}

	if (query.error) {
		return (
			<div className='h-full bg-background px-10 py-12 text-foreground'>
				<p className='text-destructive'>
					Failed to load dashboard: {query.error.message}
				</p>
			</div>
		)
	}

	if (query.isLoading || !query.data) {
		return <DashboardSkeleton />
	}

	return (
		<DashboardPage
			data={query.data}
			period={period}
			offset={offset}
			onPeriodChange={onPeriodChange}
			onOffsetChange={onOffsetChange}
		/>
	)
}

export const Route = createFileRoute('/dashboard/')({
	component: DashboardRouteGuard,
	validateSearch: zodValidator(dashboardSearchSchema),
	search: {
		middlewares: [
			stripSearchParams({period: DEFAULT_PERIOD, offset: DEFAULT_OFFSET}),
		],
	},
})
